import { z } from 'zod';

// Tipos de documento
export const documentTypes = [
  'certification',
  'license',
  'permit',
  'policy',
  'procedure',
  'contract',
  'agreement',
  'insurance',
  'audit_report',
  'compliance',
  'quality_manual',
  'work_instruction',
  'form',
  'record',
  'other',
] as const;

export type DocumentType = (typeof documentTypes)[number];

// Estados del documento
export const documentStatuses = [
  'draft',
  'pending_approval',
  'approved',
  'active',
  'expired',
  'superseded',
  'archived',
] as const;

export type DocumentStatus = (typeof documentStatuses)[number];

// Niveles de confidencialidad
export const confidentialityLevels = [
  'public',
  'internal',
  'confidential',
  'restricted',
] as const;

export type ConfidentialityLevel = (typeof confidentialityLevels)[number];

// Tipos de recordatorio
export const reminderTypes = [
  'expiration',
  'review',
  'renewal',
  'custom',
] as const;

export type ReminderType = (typeof reminderTypes)[number];

// Acciones de auditoría
export const auditActions = [
  'created',
  'updated',
  'approved',
  'rejected',
  'viewed',
  'downloaded',
  'shared',
  'archived',
  'restored',
  'deleted',
  'version_created',
] as const;

export type AuditAction = (typeof auditActions)[number];

// Schema para crear/editar documento
export const legalDocumentSchema = z.object({
  title: z.string().min(1, 'El título es requerido').max(255),
  code: z.string().max(50).optional(),
  description: z.string().optional(),
  category_id: z.string().uuid().optional().nullable(),
  document_type: z.enum(documentTypes, {
    required_error: 'Seleccione un tipo de documento',
  }),
  file_url: z.string().url().optional().nullable(),
  file_name: z.string().optional().nullable(),
  file_size: z.number().optional().nullable(),
  file_type: z.string().optional().nullable(),
  version: z.string().default('1.0'),
  effective_date: z.string().optional().nullable(),
  expiration_date: z.string().optional().nullable(),
  status: z.enum(documentStatuses).default('draft'),
  issued_by: z.string().optional().nullable(),
  review_date: z.string().optional().nullable(),
  tags: z.array(z.string()).default([]),
  keywords: z.string().optional().nullable(),
  confidentiality_level: z.enum(confidentialityLevels).default('internal'),
  notes: z.string().optional().nullable(),
});

export type LegalDocumentInput = z.infer<typeof legalDocumentSchema>;

// Schema para categoría
export const legalDocumentCategorySchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100),
  code: z.string().min(1, 'El código es requerido').max(20),
  description: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
  parent_id: z.string().uuid().optional().nullable(),
  sort_order: z.number().default(0),
});

export type LegalDocumentCategoryInput = z.infer<typeof legalDocumentCategorySchema>;

// Schema para recordatorio
export const documentReminderSchema = z.object({
  document_id: z.string().uuid(),
  reminder_type: z.enum(reminderTypes),
  reminder_date: z.string(),
  days_before: z.number().default(30),
  message: z.string().optional(),
  recipients: z.array(z.string().uuid()).default([]),
});

export type DocumentReminderInput = z.infer<typeof documentReminderSchema>;

// Interfaces para tipos de base de datos
export interface LegalDocumentCategory {
  id: string;
  name: string;
  code: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  parent_id: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LegalDocument {
  id: string;
  title: string;
  code: string | null;
  description: string | null;
  category_id: string | null;
  document_type: DocumentType;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  file_type: string | null;
  version: string;
  effective_date: string | null;
  expiration_date: string | null;
  is_current_version: boolean;
  previous_version_id: string | null;
  status: DocumentStatus;
  issued_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  review_date: string | null;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  tags: string[];
  keywords: string | null;
  confidentiality_level: ConfidentialityLevel;
  access_roles: string[] | null;
  notes: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Relaciones
  category?: LegalDocumentCategory;
}

export interface LegalDocumentAuditLog {
  id: string;
  document_id: string;
  action: AuditAction;
  action_details: Record<string, any> | null;
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  performed_by: string | null;
  performed_at: string;
  ip_address: string | null;
  user_agent: string | null;
}

export interface LegalDocumentReminder {
  id: string;
  document_id: string;
  reminder_type: ReminderType;
  reminder_date: string;
  days_before: number;
  message: string | null;
  recipients: string[];
  is_sent: boolean;
  sent_at: string | null;
  is_active: boolean;
  created_at: string;
}

// Mapeo de tipos a etiquetas en español
export const documentTypeLabels: Record<DocumentType, string> = {
  certification: 'Certificación',
  license: 'Licencia',
  permit: 'Permiso',
  policy: 'Política',
  procedure: 'Procedimiento',
  contract: 'Contrato',
  agreement: 'Acuerdo',
  insurance: 'Seguro',
  audit_report: 'Informe de Auditoría',
  compliance: 'Cumplimiento',
  quality_manual: 'Manual de Calidad',
  work_instruction: 'Instrucción de Trabajo',
  form: 'Formulario',
  record: 'Registro',
  other: 'Otro',
};

export const documentStatusLabels: Record<DocumentStatus, string> = {
  draft: 'Borrador',
  pending_approval: 'Pendiente de Aprobación',
  approved: 'Aprobado',
  active: 'Activo',
  expired: 'Vencido',
  superseded: 'Reemplazado',
  archived: 'Archivado',
};

export const confidentialityLabels: Record<ConfidentialityLevel, string> = {
  public: 'Público',
  internal: 'Interno',
  confidential: 'Confidencial',
  restricted: 'Restringido',
};

export const documentStatusColors: Record<DocumentStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  expired: 'bg-red-100 text-red-800',
  superseded: 'bg-orange-100 text-orange-800',
  archived: 'bg-slate-100 text-slate-800',
};

export const confidentialityColors: Record<ConfidentialityLevel, string> = {
  public: 'bg-green-100 text-green-800',
  internal: 'bg-blue-100 text-blue-800',
  confidential: 'bg-orange-100 text-orange-800',
  restricted: 'bg-red-100 text-red-800',
};
